import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, AlertCircle, Shield, Lock, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage({ token, onAccepted }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auth/invite-info/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) setError(data.detail);
        else setInfo(data);
      })
      .catch(() => setError("Failed to load invite details"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) { setError("Passwords don't match"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/auth/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to accept invite");

      setSuccess(true);
      // Auto-login
      if (data.token && data.user) {
        localStorage.setItem("ic_token", data.token);
        localStorage.setItem("ic_user", JSON.stringify(data.user));
        setTimeout(() => onAccepted?.(data.user), 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-emerald-700/30 bg-emerald-950/60 px-4 py-3 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-colors";

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="invite-loading">
        <Loader2 className="h-6 w-6 text-gold animate-spin" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="invite-success">
        <div className="absolute inset-0 bg-emerald-950" />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative z-10 text-center max-w-md px-4">
          <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-cream mb-2">Welcome to the Team!</h2>
          <p className="text-sm text-cream/60">Your account is active. Redirecting to your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  if (!info || info.status !== "pending") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="invite-invalid">
        <div className="absolute inset-0 bg-emerald-950" />
        <div className="relative z-10 text-center max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-cream mb-2">Invite Unavailable</h2>
          <p className="text-sm text-cream/60">{error || "This invite has expired, been revoked, or already accepted."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center relative" data-testid="accept-invite-page">
      <div className="absolute inset-0 bg-emerald-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,201,139,0.06),transparent_60%)]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md mx-4 rounded-2xl border border-emerald-700/30 bg-emerald-900/40 backdrop-blur-xl p-8">

        <div className="text-center mb-6">
          <div className="mx-auto h-14 w-14 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-4">
            <Shield className="h-7 w-7 text-gold" />
          </div>
          <h2 className="text-xl font-bold text-cream">Join Imaani Capital</h2>
          <p className="text-sm text-cream/50 mt-1">You've been invited as <span className="text-gold font-semibold">{info.role_label}</span></p>
        </div>

        <div className="rounded-xl border border-emerald-700/20 bg-emerald-950/40 p-4 mb-6 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-cream/50">Name</span>
            <span className="text-cream font-medium">{info.full_name}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-cream/50">Email</span>
            <span className="text-cream font-medium">{info.email}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-cream/50">Role</span>
            <span className="text-gold font-medium">{info.role_label}</span>
          </div>
        </div>

        <form onSubmit={handleAccept} className="space-y-4" data-testid="accept-invite-form">
          <label className="block">
            <span className="block text-xs font-medium text-cream/70 mb-1.5 uppercase tracking-wider">Set Your Password</span>
            <div className="relative">
              <input required type={showPw ? "text" : "password"} value={password}
                onChange={e => setPassword(e.target.value)} minLength={8}
                className={inputCls + " pr-12"} placeholder="Min 8 characters" data-testid="invite-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream/70">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-cream/70 mb-1.5 uppercase tracking-wider">Confirm Password</span>
            <input required type="password" value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)} minLength={8}
              className={inputCls} placeholder="Confirm password" data-testid="invite-confirm-password" />
          </label>

          {error && (
            <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-xs text-red-300 flex items-center gap-2">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
            </div>
          )}

          <button type="submit" disabled={submitting || !password || password !== confirmPw}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold to-gold-dark py-3 px-6 text-sm font-bold text-[#022c22] hover:from-gold/90 hover:to-gold-dark/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            data-testid="accept-invite-submit">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Setting up...</> : <><Lock className="h-4 w-4" /> Activate My Account</>}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
